import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, useTheme, useMediaQuery } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { bannerApi } from '../../api/banner';
import { useNavigate } from 'react-router-dom';
import { REFERRER_TYPE } from '../../constants/gaEnums';
import { useUserStore } from '../../stores/userStore';
import { trackBannerClick, trackBannerImpression } from '../../lib/gaEvents';
import FeedBannerContent, { feedBannerPath, visibleWebBanners } from './FeedBannerContent';

const BannerCarousel = ({ pageType }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const [allBanners, setAllBanners] = useState({ PC: [], MOBILE: [] });
  const swiperRef = useRef(null);
  const impressedBanners = useRef(new Set());
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const data = await bannerApi.getBanners();
        setAllBanners(data);
      } catch (e) {}
    };
    fetchBanners();
  }, []);

  const banners = React.useMemo(
    () => visibleWebBanners(isXs ? allBanners.MOBILE : allBanners.PC),
    [isXs, allBanners],
  );

  useEffect(() => {
    if (banners.length === 0) return;
    const banner = banners[0];
    if (!impressedBanners.current.has(banner.id)) {
      impressedBanners.current.add(banner.id);
      trackBannerImpression(
        { bannerId: banner.id, bannerTitle: banner.title, bannerLocation: 'home_top' },
        user,
      );
    }
  }, [banners, user]);

  const handleSlideChange = (swiper) => {
    const banner = banners[swiper.realIndex];
    if (banner && !impressedBanners.current.has(banner.id)) {
      impressedBanners.current.add(banner.id);
      trackBannerImpression(
        { bannerId: banner.id, bannerTitle: banner.title, bannerLocation: 'home_top' },
        user,
      );
    }
  };

  const handleClick = (banner) => {
    bannerApi.trackClick(banner.id).catch(() => {});
    trackBannerClick(
      { bannerId: banner.id, bannerTitle: banner.title, bannerLocation: 'home_top', pageType },
      user,
    );
    const feedDestination = feedBannerPath(banner);
    if (feedDestination) {
      navigate(feedDestination, { state: { referrerType: REFERRER_TYPE.BANNER } });
      return;
    }
    if (!banner.url) return;
    const isInternal = banner.url.startsWith('/') || banner.url.startsWith(window.location.origin);
    if (isInternal) {
      const path = banner.url.startsWith('/')
        ? banner.url
        : banner.url.slice(window.location.origin.length);
      navigate(path, { state: { referrerType: REFERRER_TYPE.BANNER } });
    } else {
      window.open(banner.url, '_blank', 'noopener,noreferrer');
    }
  };

  if (banners.length === 0) return null;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '1180px',
        mx: 'auto',
        position: 'relative',
        overflow: 'hidden',
        '& .swiper-pagination': {
          bottom: '16px !important',
        },
        '& .swiper-pagination-bullet': {
          width: 8,
          height: 8,
          backgroundColor: theme.palette.black[3],
          opacity: 1,
          transition: 'background-color 0.3s',
        },
        '& .swiper-pagination-bullet-active': {
          backgroundColor: theme.palette.secondary.main,
        },
      }}
    >
      <Swiper
        modules={[Autoplay, Navigation, Pagination]}
        pagination={isXs ? false : { clickable: true }}
        autoplay={{ delay: 3000, disableOnInteraction: false }}
        loop={banners.length >= 2}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={handleSlideChange}
        style={{ width: '100%' }}
      >
        {banners.map((banner) => (
          <SwiperSlide key={banner.id}>
            <Box
              onClick={() => handleClick(banner)}
              role={feedBannerPath(banner) ? 'link' : undefined}
              tabIndex={feedBannerPath(banner) ? 0 : undefined}
              aria-label={
                feedBannerPath(banner) ? banner.appConfig.headline || banner.title : undefined
              }
              onKeyDown={(event) => {
                if (feedBannerPath(banner) && event.key === 'Enter') handleClick(banner);
              }}
              sx={{
                cursor: banner.url ? 'pointer' : 'default',
                width: '100%',
                aspectRatio: { xs: '360 / 100', md: '1180 / 200' },
                overflow: 'hidden',
              }}
            >
              {feedBannerPath(banner) ? (
                <FeedBannerContent banner={banner} />
              ) : (
                <Box
                  component="img"
                  src={banner.imageUrl}
                  alt={banner.title || '배너'}
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    objectFit: 'cover',
                  }}
                />
              )}
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>

      {banners.length >= 2 && (
        <>
          <IconButton
            onClick={() => swiperRef.current?.slidePrev()}
            sx={{
              position: 'absolute',
              left: -10,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              color: theme.palette.black[3],
              display: { xs: 'none', md: 'flex' },
              '&:hover': { backgroundColor: 'transparent' },
            }}
            disableRipple
          >
            <NavigateBeforeIcon sx={{ fontSize: 44 }} />
          </IconButton>
          <IconButton
            onClick={() => swiperRef.current?.slideNext()}
            sx={{
              position: 'absolute',
              right: -10,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              color: theme.palette.black[3],
              display: { xs: 'none', md: 'flex' },
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <NavigateNextIcon sx={{ fontSize: 44 }} />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default BannerCarousel;
